import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./MultiSelectFilter.module.css";

export default function MultiSelectFilter({
  dropdownTitle,
  placeholder = "Type to search...",
  fetchData,
  selected = [],
  onSelectionChange,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [internalSelected, setInternalSelected] = useState(selected);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isInputActive, setIsInputActive] = useState(false);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef({}); // cache per query
  const [dropdownPosition, setDropdownPosition] = useState({ top: '110%', left: 0, transform: 'none' });

  /* ---------------- Sync external selection ---------------- */
  useEffect(() => {
    console.log('MultiSelectFilter selected prop:', selected);
    setInternalSelected(selected);
  }, [selected]);

  /* ---------------- Calculate dropdown position ---------------- */
  const calculateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current || !open) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    const dropdownHeight = 300; // Estimated max height
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const containerRect = wrapperRef.current.parentElement?.getBoundingClientRect();
    
    let newTop = '110%';
    let newLeft = 0;
    let newTransform = 'none';
    
    // Check if dropdown would go below viewport
    if (rect.bottom + dropdownHeight > viewportHeight) {
      if (rect.top > dropdownHeight) {
        // Show above if there's enough space
        newTop = 'auto';
        newTransform = 'translateY(-100%)';
      } else {
        // Show below but limit height if not enough space above
        newTop = '110%';
      }
    }
    
    // Check if this is the last item in the row or if dropdown would overflow
    const isLastInRow = containerRect && (rect.right + 250 >= containerRect.right || rect.right + 250 >= viewportWidth);
    
    if (isLastInRow) {
      // Align to right for last items to prevent overflow
      newLeft = 'auto';
      newTransform = newTransform === 'translateY(-100%)' ? 'translateY(-100%) translateX(-40%)' : 'translateX(-40%)';
    } else {
      // Default left alignment for other items
      newLeft = 0;
      if (newTransform.includes('translateX(-40%)')) {
        newTransform = newTransform.replace('translateX(-40%)', '');
      }
    }
    
    setDropdownPosition({ top: newTop, left: newLeft, transform: newTransform });
  }, [open]);
  
  useEffect(() => {
    calculateDropdownPosition();
    window.addEventListener('resize', calculateDropdownPosition);
    return () => window.removeEventListener('resize', calculateDropdownPosition);
  }, [calculateDropdownPosition]);

  /* ---------------- Outside click close ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setIsInputActive(false);
        setQuery("");
        setSuggestions([]);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- Fetch suggestions (cached) ---------------- */
  const fetchSuggestions = useCallback(
    (search) => {
      if (!fetchData) return;

      if (cacheRef.current[search]) {
        setSuggestions(cacheRef.current[search]);
        setLoading(false);
        return;
      }

      setLoading(true);

      fetchData(search)
        .then((data) => {
          cacheRef.current[search] = data;
          setSuggestions(data);
        })
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    },
    [fetchData]
  );

  /* ---------------- Debounced fetch ONLY on input interaction ---------------- */
  useEffect(() => {
    if (!open || !isInputActive) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query || "");
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [query, open, isInputActive, fetchSuggestions]);

  /* ---------------- Helpers ---------------- */
  const getValue = (item) =>
    typeof item === "object" ? item.value || item.label : item;

  const isSelected = (item) =>
    internalSelected.some((s) => getValue(s) === getValue(item));

  const addItem = (item) => {
    if (isSelected(item)) return;

    const updated = [...internalSelected, item];
    setInternalSelected(updated);
    
    // Always extract values for API calls
    const dataToSend = updated.map(item => getValue(item));
    console.log('MultiSelectFilter sending data:', dataToSend);
    onSelectionChange?.(dataToSend);

    setQuery("");
    setHighlightIndex(-1);
  };

  const removeItem = (item) => {
    const updated = internalSelected.filter(
      (s) => getValue(s) !== getValue(item)
    );
    setInternalSelected(updated);
    
    // Always extract values for API calls
    const dataToSend = updated.map(item => getValue(item));
    console.log('MultiSelectFilter sending data (remove):', dataToSend);
    onSelectionChange?.(dataToSend);
  };

  /* ---------------- Keyboard navigation ---------------- */
  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const item = suggestions[highlightIndex];
      if (item && !isSelected(item)) addItem(item);
    }

    if (e.key === "Escape") {
      setOpen(false);
      setIsInputActive(false);
    }
  };

  /* ---------------- Render ---------------- */
  return (
    <div className={styles.filterWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.filterBtn}
        onClick={() => setOpen((o) => !o)}
      >
        {dropdownTitle}
        {internalSelected.length > 0 && (
          <span className={styles.countBadge}>{internalSelected.length}</span>
        )}
      </button>

      {open && (
        <div 
          className={styles.dropdown}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            transform: dropdownPosition.transform
          }}
        >
          {/* Selected Chips */}
          <div className={styles.selectedChips}>
            {internalSelected.map((s, i) => (
              <span key={getValue(s) + i} className={styles.chip}>
                {s.label || s}
                <button onClick={() => removeItem(s)}>×</button>
              </span>
            ))}
          </div>

          {/* Input */}
          <input
            type="text"
            value={query}
            placeholder={placeholder}
            onFocus={() => {
              setIsInputActive(true);
              setOpen(true);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsInputActive(true);
            }}
            onKeyDown={handleKeyDown}
            className={styles.input}
            autoFocus
          />

          {/* Loading */}
          {isInputActive && loading && (
            <div className={styles.loading}>Loading...</div>
          )}

          {/* Suggestions */}
          {isInputActive && !loading && suggestions.length > 0 && (
            <ul className={styles.suggestions}>
              {suggestions.map((s, idx) => {
                const disabled = isSelected(s);
                return (
                  <li
                    key={getValue(s)}
                    className={`${disabled ? styles.disabled : ""} ${
                      idx === highlightIndex ? styles.highlight : ""
                    }`}
                    onClick={() => !disabled && addItem(s)}
                  >
                    {s.label || s}
                    {disabled && " ✓"}
                  </li>
                );
              })}
            </ul>
          )}

          {/* No results */}
          {isInputActive && !loading && suggestions.length === 0 && (
            <div className={styles.noResults}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
